<template>
  <div
    class="layout-content"
    :class="{
      'tall-layout-content': tall,
      'stacked-tabs': stacked,
      'reversed-tabs': reversed,
      'grow-tabs': grow,
    }"
  >
    <v-tabs :value.sync="currentTabIndex" color="white" background-color="transparent" hide-slider>
      <v-tab
        v-for="item in _items"
        :key="item.text"
        active-class="active-tab"
        :data-test-tooltip="item.dataTestTooltip"
        :to="item.to"
        @click="
          (clickEvent) => {
            click(clickEvent, item);
          }
        "
      >
        <v-icon v-if="item.icon" x-small class="mr-2">{{ item.icon }}</v-icon>
        <div>{{ item.text }}</div>
        <v-tooltip v-if="item.appendText" bottom>
          {{ item.appendText }}
          <template #activator="{ on }">
            <v-btn
              class="plus-button"
              icon
              x-small
              v-on="on"
              @click.prevent.stop="
                (clickEvent) => {
                  clickAppend(clickEvent, item);
                }
              "
            >
              <v-icon small>$yellow-plus</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </v-tab>
    </v-tabs>
  </div>
</template>

<script lang="ts">
import { Component, Prop, PropSync, Vue } from "vue-property-decorator";
import TopMenuItemModel from "@/models/components/layout/TopMenuItemModel";
import ClickAction from "vuetify/types";

@Component
export default class LayoutContentMenu extends Vue {
  @Prop({ required: true })
  readonly items!: Array<TopMenuItemModel>;

  get _items(): Array<TopMenuItemModel> {
    if (this.reversed) return this.items.reverse();
    else return this.items;
  }

  @PropSync("selected", { required: false, default: undefined })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _selected?: any;

  @Prop({ required: false, default: false })
  white!: boolean;

  @Prop({ required: false, default: false })
  defaultAction!: boolean;

  @Prop({ required: false, default: false })
  tall!: boolean;

  @Prop({ required: false, default: false })
  stacked!: boolean;

  @Prop({ required: false, default: false })
  reversed!: boolean;

  @Prop({ required: false, default: false })
  grow!: boolean;

  get currentTabIndex(): number | undefined {
    if (typeof this._selected !== "undefined") {
      for (const itemIndex in this.items) {
        if (typeof this.items[itemIndex].value !== "undefined" && this.items[itemIndex].value === this._selected) {
          return parseFloat(itemIndex);
        }
      }
    }
    return undefined;
  }

  click!: (clickAction: ClickAction, item: TopMenuItemModel) => void;

  clickAppend = (clickAction: ClickAction, item: TopMenuItemModel): void => {
    if (typeof item.appendAction === "function") {
      item.appendAction(item, this, clickAction);
    }
  };

  created(): void {
    const defaultActionImplementation = (item: TopMenuItemModel) => {
      this._selected = item.value;
    };

    this.click = (clickAction: ClickAction, item: TopMenuItemModel): void => {
      if (typeof item.action === "function") {
        item.action(item, this, clickAction);
      }
      if (this.defaultAction) {
        defaultActionImplementation(item);
      }
    };
  }
}
</script>
<style scoped lang="scss">
.layout-content .v-tab {
  border-radius: 8px 8px 0 0;
  height: 25px;
  text-transform: none !important;
  font-size: 12px;
  padding: 0 28px;
  color: $base-darken !important;
  font-weight: 600;
  letter-spacing: 0;
}

.layout-content .v-tab:not(.v-tab--active) {
  border: 1px solid $tab-border-color;
  background-color: $container-background-color;
}

.layout-content {
  padding: 0 24px;
}

.layout-content .v-tab:not(.v-tab--active) .v-icon {
  color: $base-darken !important;
}

.layout-content .v-tab::before {
  border: 0px;
  border-radius: 8px 8px 0 0;
  position: absolute;
  left: -1px;
  right: -1px;
  top: -1px;
  bottom: 0px;
}

.layout-content .v-tab:focus::before {
  opacity: 0.12 !important;
}

.layout-content .active-tab {
  background-color: $base-alternative;
  color: map-get($shades, "white") !important;
}

.layout-content .v-tabs {
  height: 24px;
  overflow: hidden;
}

.plus-button {
  margin-left: 24px;
  margin-right: -22px;
  padding: 1px;
}

.layout-content-text {
  font-weight: 600;
  letter-spacing: 0;
}

.layout-content.tall-layout-content .v-tab {
  padding: 0 14px;
}

.layout-content .v-slide-group__prev {
  height: 28px;
}

.layout-content .v-tabs-bar {
  height: 28px;
}

.layout-content .v-slide-group__prev {
  color: $base-darken;
}

.stacked-tabs ::v-deep .v-slide-group__content.v-tabs-bar__content {
  width: 100%;
  margin-bottom: 2px;
  flex-wrap: wrap;
}

.reversed-tabs ::v-deep .v-slide-group__content.v-tabs-bar__content {
  @for $i from 1 through 20 {
    :nth-child(#{$i}) {
      z-index: #{20 - $i};
    }
  }
  flex-wrap: wrap-reverse !important;
  flex-direction: row-reverse;
}

.stacked-tabs .v-tabs-bar {
  height: 50px !important;
}

.stacked-tabs .v-tabs {
  height: initial !important;
}

.stacked-tabs .v-tab {
  height: 32px;
  padding: 4px 24px 8px;
  border-radius: 8px 8px 0 0;
  margin: 2px 0 -9px;
}

.grow-tabs .v-tab {
  flex-grow: 1;
}

.stacked-tabs {
  padding: 0;
}
</style>
